import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { InfiniteData } from '@tanstack/react-query';

import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { docHetTrang } from '../../../lib/api/phan-trang';
import { soVN } from '../../../lib/dinh-dang';
import type { ApiError } from '../../../lib/errors';
import { triThucRepository } from '../../../lib/repositories/tri-thuc.repository';
import { useToast } from '../../../lib/toast';
import { MOT_TRANG, THAO_TAC_TAI_LIEU, TRANG_TEP } from '../constants';
import type { ThaoTacTaiLieu } from '../nghiep-vu/tri-thuc';
import type {
  DoiTuongQuyen,
  Folder,
  KhoSua,
  KhoTao,
  KnowledgeBase,
  KnowledgeDocument,
  KnowledgeDocumentDetail,
  KnowledgeFile,
  LoaiTep,
  QuyenDocMoi,
  ReadAccess,
  TepSua,
  TepTaiLen,
  Trang,
  TrangThaiTaiLieu,
} from '../types';

/** Khoá cache: mọi thứ của module nằm dưới `['tri-thuc']`, vô hiệu hoá theo nhánh. */
export const KHOA_TRI_THUC = {
  kho: ['tri-thuc', 'kho'] as const,
  khoLoc: (search: string, dept: string | undefined) => ['tri-thuc', 'kho', 'ds', search, dept ?? 'tat-ca'] as const,
  khoTatCa: ['tri-thuc', 'kho', 'tat-ca'] as const,
  thongKe: ['tri-thuc', 'kho', 'thong-ke'] as const,
  chiTietKho: (id: string) => ['tri-thuc', 'kho', 'chi-tiet', id] as const,
  thuMuc: (kbId: string) => ['tri-thuc', 'thu-muc', kbId] as const,
  tep: ['tri-thuc', 'tep'] as const,
  tepLoc: (kbId: string, thuMuc: string | undefined, search: string, loai: LoaiTep | undefined) =>
    ['tri-thuc', 'tep', kbId, thuMuc ?? 'tat-ca', search, loai ?? 'moi-loai'] as const,
  quyenDocGoc: ['tri-thuc', 'quyen-doc'] as const,
  quyenDoc: (loai: DoiTuongQuyen['loai'], id: string) => ['tri-thuc', 'quyen-doc', loai, id] as const,
  taiLieu: ['tri-thuc', 'tai-lieu'] as const,
  taiLieuLoc: (status: string | undefined, kbId: string | undefined, search: string) =>
    ['tri-thuc', 'tai-lieu', 'ds', status ?? 'tat-ca', kbId ?? 'tat-ca', search] as const,
  chiTietTaiLieu: (id: string) => ['tri-thuc', 'tai-lieu', 'chi-tiet', id] as const,
  phongBan: ['tri-thuc', 'danh-ba', 'phong-ban'] as const,
  nguoiTheoPhongBan: (dept: string) => ['tri-thuc', 'danh-ba', 'nguoi', dept] as const,
};

/** 4xx là câu trả lời của server, không phải trục trặc: thử lại chỉ bắt người dùng chờ thêm. */
function thuLai(soLanDaThu: number, err: unknown): boolean {
  const status = (err as ApiError | undefined)?.status;
  if (status !== undefined && status >= 400 && status < 500) return false;
  return soLanDaThu < 1;
}

function useDanhSachVoHan<T>(queryKey: readonly unknown[], doc: (cursor: string | undefined) => Promise<Trang<T>>,
  tuyChon: { refetchInterval?: (du: InfiniteData<Trang<T>> | undefined) => number | false; enabled?: boolean } = {}) {
  const query = useInfiniteQuery<Trang<T>, ApiError, InfiniteData<Trang<T>, string | undefined>, readonly unknown[], string | undefined>({
    queryKey,
    queryFn: ({ pageParam }) => doc(pageParam),
    initialPageParam: undefined,
    getNextPageParam: (trang) => trang.next_cursor ?? undefined,
    staleTime: 30_000,
    retry: thuLai,
    ...(tuyChon.enabled !== undefined ? { enabled: tuyChon.enabled } : {}),
    ...(tuyChon.refetchInterval ? { refetchInterval: (q) => tuyChon.refetchInterval!(q.state.data) } : {}),
  });
  const dong = query.data?.pages.flatMap((p) => p.items) ?? [];
  return { ...query, dong };
}

// ─────────────── Đọc ───────────────

export function useDanhSachKho(search: string, dept: string | undefined) {
  return useDanhSachVoHan<KnowledgeBase>(KHOA_TRI_THUC.khoLoc(search, dept), (cursor) =>
    triThucRepository.danhSachKho({ limit: MOT_TRANG, cursor, ...(search ? { search } : {}), ...(dept ? { department_id: dept } : {}) }));
}

/** Mọi bộ, nối hết trang — cho ô chọn bộ (tải tài liệu, chuyển tệp sang bộ khác). */
export function useTatCaKho(bat = true) {
  return useQuery<KnowledgeBase[], ApiError>({
    queryKey: KHOA_TRI_THUC.khoTatCa,
    queryFn: () => docHetTrang((cursor) => triThucRepository.danhSachKho({ limit: 100, cursor })),
    staleTime: 30_000,
    retry: thuLai,
    enabled: bat,
  });
}

export function useThongKe() {
  return useQuery({ queryKey: KHOA_TRI_THUC.thongKe, queryFn: () => triThucRepository.thongKe(), staleTime: 30_000, retry: thuLai });
}

export function useChiTietKho(id: string) {
  return useQuery<KnowledgeBase, ApiError>({
    queryKey: KHOA_TRI_THUC.chiTietKho(id),
    queryFn: () => triThucRepository.chiTietKho(id),
    staleTime: 30_000,
    retry: thuLai,
  });
}

export function useThuMuc(kbId: string) {
  return useQuery<{ items: Folder[] }, ApiError>({
    queryKey: KHOA_TRI_THUC.thuMuc(kbId),
    queryFn: () => triThucRepository.danhSachThuMuc(kbId),
    staleTime: 30_000,
    retry: thuLai,
  });
}

/** Còn tệp đang chờ hay đang nạp (chưa kẹt): hỏi lại mỗi 4 giây để Badge đổi theo hàng đợi của module. */
function conDangNap(du: InfiniteData<Trang<KnowledgeFile>> | undefined): number | false {
  const dang = du?.pages.some((p) => p.items.some((f) => (f.index_status === 'CHO_INDEX' || f.index_status === 'DANG_INDEX')
    && !f.index_stuck));
  return dang ? 4000 : false;
}

/** `thuMuc`: `undefined` là mọi tệp của bộ, `root` là tệp ở gốc bộ. */
export function useDanhSachTep(kbId: string, thuMuc: string | undefined, search: string, loai: LoaiTep | undefined) {
  return useDanhSachVoHan<KnowledgeFile>(KHOA_TRI_THUC.tepLoc(kbId, thuMuc, search, loai), (cursor) =>
    triThucRepository.danhSachTep({ limit: TRANG_TEP, cursor, knowledge_base_id: kbId, ...(thuMuc ? { folder_id: thuMuc } : {}),
      ...(search ? { search } : {}), ...(loai ? { file_type: loai } : {}) }), { refetchInterval: conDangNap });
}

/** Quyền đọc của một tệp hay thư mục: luôn đọc mới khi mở hộp, tầng trên có thể vừa đổi. */
export function useQuyenDoc(kbId: string, doiTuong: DoiTuongQuyen, bat: boolean) {
  const id = doiTuong.loai === 'tep' ? doiTuong.tep.id : doiTuong.thuMuc.id;
  return useQuery<ReadAccess, ApiError>({
    queryKey: KHOA_TRI_THUC.quyenDoc(doiTuong.loai, id),
    queryFn: () => (doiTuong.loai === 'tep' ? triThucRepository.quyenDocTep(id) : triThucRepository.quyenDocThuMuc(kbId, id)),
    enabled: bat,
    gcTime: 0,
    retry: thuLai,
  });
}

export function useDanhSachTaiLieu(status: TrangThaiTaiLieu | undefined, kbId: string | undefined, search: string) {
  return useDanhSachVoHan<KnowledgeDocument>(KHOA_TRI_THUC.taiLieuLoc(status, kbId, search), (cursor) =>
    triThucRepository.danhSachTaiLieu({ limit: MOT_TRANG, cursor, ...(status ? { status } : {}), ...(kbId ? { knowledge_base_id: kbId } : {}),
      ...(search ? { search } : {}) }));
}

export function useChiTietTaiLieu(id: string | undefined) {
  return useQuery<KnowledgeDocumentDetail, ApiError>({
    queryKey: KHOA_TRI_THUC.chiTietTaiLieu(id ?? ''),
    queryFn: () => triThucRepository.chiTietTaiLieu(id!),
    enabled: Boolean(id),
    retry: thuLai,
  });
}

export function usePhongBan() {
  return useQuery({ queryKey: KHOA_TRI_THUC.phongBan, queryFn: () => triThucRepository.phongBan(), staleTime: 60_000, retry: thuLai });
}

/** Tải lười người của một phòng ban cho cây: cùng cache giữa các lần mở hộp, hỏng thì ném để nơi gọi đặt lỗi cạnh cây. */
export function useTaiNguoiPhongBan() {
  const queryClient = useQueryClient();
  return (dept: string) => queryClient.fetchQuery({
    queryKey: KHOA_TRI_THUC.nguoiTheoPhongBan(dept),
    queryFn: () => docHetTrang((cursor) => triThucRepository.nguoiTheoPhongBan(dept, cursor)),
    staleTime: 60_000,
    retry: thuLai,
  });
}

// ─────────────── Ghi ───────────────

function useLamMoi() {
  const queryClient = useQueryClient();
  return (...nhanh: ReadonlyArray<readonly unknown[]>) => {
    for (const queryKey of nhanh) void queryClient.invalidateQueries({ queryKey });
  };
}

/**
 * Mở URL ký ngắn hạn để tải xuống. Thẻ `<a download>` thay `window.open`: URL `data:` của dữ liệu
 * mẫu bị trình duyệt chặn khi mở tab mới, còn URL ký của object storage tự trả `Content-Disposition:
 * attachment` nên tải xuống mà không rời trang.
 */
function moTaiXuong(url: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = '';
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function useTaoKho() {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi();
  return useMutation<KnowledgeBase, ApiError, KhoTao>({
    mutationFn: (du) => triThucRepository.taoKho(du),
    onSuccess: (kb) => {
      lamMoi(KHOA_TRI_THUC.kho);
      toast.success(t('triThuc.kho.daTao', { ten: kb.name }));
    },
  });
}

export function useSuaKho() {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi();
  return useMutation<KnowledgeBase, ApiError, { id: string; du: KhoSua }>({
    mutationFn: ({ id, du }) => triThucRepository.suaKho(id, du),
    onSuccess: (kb) => {
      lamMoi(KHOA_TRI_THUC.kho);
      toast.success(t('triThuc.kho.daLuu', { ten: kb.name }));
    },
  });
}

export function useXoaKho() {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi();
  return useMutation<void, ApiError, KnowledgeBase>({
    mutationFn: (kb) => triThucRepository.xoaKho(kb.id),
    onSuccess: (_v, kb) => {
      lamMoi(KHOA_TRI_THUC.kho, KHOA_TRI_THUC.tep);
      toast.success(t('triThuc.kho.daXoa', { ten: kb.name }));
    },
  });
}

export function useTaoThuMuc(kbId: string) {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi();
  return useMutation<Folder, ApiError, { name: string; parent_id: string | null }>({
    mutationFn: (du) => triThucRepository.taoThuMuc(kbId, du),
    onSuccess: (f) => {
      lamMoi(KHOA_TRI_THUC.thuMuc(kbId));
      toast.success(t('triThuc.thuMuc.daTao', { ten: f.name }));
    },
  });
}

export function useDoiTenThuMuc(kbId: string) {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi();
  return useMutation<Folder, ApiError, { id: string; name: string }>({
    mutationFn: ({ id, name }) => triThucRepository.doiTenThuMuc(kbId, id, name),
    onSuccess: (f) => {
      lamMoi(KHOA_TRI_THUC.thuMuc(kbId));
      toast.success(t('triThuc.thuMuc.daDoiTen', { ten: f.name }));
    },
  });
}

export function useXoaThuMuc(kbId: string) {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi();
  return useMutation<void, ApiError, Folder>({
    mutationFn: (f) => triThucRepository.xoaThuMuc(kbId, f.id),
    onSuccess: (_v, f) => {
      lamMoi(KHOA_TRI_THUC.thuMuc(kbId), KHOA_TRI_THUC.tep);
      toast.success(t('triThuc.thuMuc.daXoa', { ten: f.name }));
    },
  });
}

/** Kết quả tải nhiều tệp: tệp nào lỗi giữ lại kèm lỗi để người dùng sửa và gửi lại. */
export interface KetQuaTaiTep {
  daTai: KnowledgeFile[];
  loi: Array<{ tep: File; loi: ApiError; daTaiLen: boolean }>;
}

/**
 * Tải lần lượt từng tệp vào một bộ (`POST /files` nhận một tệp mỗi yêu cầu). Backend chưa nhận thư mục
 * lúc tải lên, nên tệp tải khi đang mở một thư mục được chuyển vào đó ngay sau bằng `PATCH`; lần chuyển
 * này hỏng thì tệp vẫn đã lên (`daTaiLen`) và nằm ở gốc bộ — gửi lại sẽ thành hai bản.
 */
export function useTaiNhieuTep() {
  const toast = useToast();
  const { t, locale } = useT();
  const lamMoi = useLamMoi();
  return useMutation<KetQuaTaiTep, ApiError, { du: Omit<TepTaiLen, 'tep'>; tep: File[]; thuMuc: string | null }>({
    mutationFn: async ({ du, tep, thuMuc }) => {
      const kq: KetQuaTaiTep = { daTai: [], loi: [] };
      for (const f of tep) {
        let daLen: KnowledgeFile | undefined;
        try {
          daLen = await triThucRepository.taiTep({ ...du, tep: f });
          kq.daTai.push(thuMuc ? await triThucRepository.suaTep(daLen.id, { folder_id: thuMuc }) : daLen);
        } catch (err) {
          kq.loi.push({ tep: f, loi: err as ApiError, daTaiLen: daLen !== undefined });
        }
      }
      return kq;
    },
    onSuccess: (kq) => {
      lamMoi(KHOA_TRI_THUC.tep, KHOA_TRI_THUC.kho);
      if (kq.daTai.length === 1) toast.success(t('triThuc.tep.daTaiLen', { ten: kq.daTai[0]!.file_name }));
      else if (kq.daTai.length > 1) toast.success(t('triThuc.tep.daTaiLenNhieu', { so: soVN(kq.daTai.length, locale) }));
    },
  });
}

export function useSuaTep() {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi();
  return useMutation<KnowledgeFile, ApiError, { tep: KnowledgeFile; du: TepSua; daXong: 'hieuLuc' | 'thuMuc' }>({
    mutationFn: ({ tep, du }) => triThucRepository.suaTep(tep.id, du),
    onSuccess: (f, { daXong }) => {
      lamMoi(KHOA_TRI_THUC.tep, KHOA_TRI_THUC.quyenDocGoc);
      toast.success(t(daXong === 'hieuLuc' ? 'triThuc.tep.daLuuHieuLuc' : 'triThuc.tep.daChuyenThuMuc', { ten: f.file_name }));
    },
  });
}

export function useChuyenBo() {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi();
  return useMutation<KnowledgeFile, ApiError, { tep: KnowledgeFile; kbId: string | null }>({
    mutationFn: ({ tep, kbId }) => triThucRepository.chuyenBo(tep.id, kbId),
    onSuccess: (f) => {
      lamMoi(KHOA_TRI_THUC.tep, KHOA_TRI_THUC.kho);
      toast.success(f.knowledge_base_name
        ? t('triThuc.tep.daChuyenBo', { ten: f.file_name, bo: f.knowledge_base_name })
        : t('triThuc.tep.daBoKhoiBo', { ten: f.file_name }));
    },
  });
}

/**
 * "Đồng bộ lại": bấm thẳng từ menu, không có hộp nào để đặt lỗi vào, nên lỗi đi bằng toast kèm câu của
 * server (`409` nói tệp đang nạp, `503` nói index chưa sẵn sàng).
 */
export function useDongBoLai() {
  const toast = useToast();
  const { t } = useT();
  const dichLoi = useLoi();
  const lamMoi = useLamMoi();
  return useMutation<KnowledgeFile, ApiError, KnowledgeFile>({
    mutationFn: (f) => triThucRepository.dongBoLai(f.id),
    onSuccess: (f) => {
      lamMoi(KHOA_TRI_THUC.tep);
      toast.success(t('triThuc.tep.daXepDongBo', { ten: f.file_name }));
    },
    onError: (err, f) => toast.error(t('triThuc.tep.loiDongBo', { ten: f.file_name, loi: dichLoi(err) })),
  });
}

export function useXoaTep() {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi();
  return useMutation<void, ApiError, KnowledgeFile>({
    mutationFn: (f) => triThucRepository.xoaTep(f.id),
    onSuccess: (_v, f) => {
      lamMoi(KHOA_TRI_THUC.tep, KHOA_TRI_THUC.kho);
      toast.success(t('triThuc.tep.daXoa', { ten: f.file_name }));
    },
  });
}

/** Tải xuống tệp hay tài liệu: xin URL ký rồi mở ngay. Lỗi đi bằng toast, cùng lý do với "Đồng bộ lại". */
export function useTaiXuong() {
  const toast = useToast();
  const { t } = useT();
  const dichLoi = useLoi();
  return useMutation<void, ApiError, { loai: 'tep' | 'tai-lieu'; id: string; ten: string }>({
    mutationFn: async ({ loai, id }) => {
      const kq = loai === 'tep' ? await triThucRepository.taiXuongTep(id) : await triThucRepository.taiXuongTaiLieu(id);
      moTaiXuong(kq.url);
    },
    onError: (err, { ten }) => toast.error(t('triThuc.loiTaiXuong', { ten, loi: dichLoi(err) })),
  });
}

export function useDatQuyenDoc(kbId: string) {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi();
  return useMutation<ReadAccess, ApiError, { doiTuong: DoiTuongQuyen; du: QuyenDocMoi }>({
    mutationFn: ({ doiTuong, du }) => (doiTuong.loai === 'tep'
      ? triThucRepository.datQuyenDocTep(doiTuong.tep.id, du)
      : triThucRepository.datQuyenDocThuMuc(kbId, doiTuong.thuMuc.id, du)),
    onSuccess: (_v, { doiTuong }) => {
      // Tệp và thư mục con đang kế thừa cũng đổi nguồn: tải lại cả hai nhánh.
      lamMoi(KHOA_TRI_THUC.tep, KHOA_TRI_THUC.thuMuc(kbId), KHOA_TRI_THUC.quyenDocGoc);
      toast.success(t('triThuc.quyen.daLuu', { ten: doiTuong.loai === 'tep' ? doiTuong.tep.file_name : doiTuong.thuMuc.name }));
    },
  });
}

export function useChuyenTaiLieu() {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi();
  return useMutation<KnowledgeDocumentDetail, ApiError, { taiLieu: KnowledgeDocument; thaoTac: ThaoTacTaiLieu; note?: string }>({
    mutationFn: ({ taiLieu, thaoTac, note }) => triThucRepository.chuyenTaiLieu(taiLieu.id, thaoTac, note),
    onSuccess: (d, { thaoTac }) => {
      // Duyệt sinh tệp trong bộ: bảng tệp và số tệp của bộ cũng đổi
      lamMoi(KHOA_TRI_THUC.taiLieu, KHOA_TRI_THUC.tep, KHOA_TRI_THUC.kho);
      toast.success(t(THAO_TAC_TAI_LIEU[thaoTac].daXong, { ten: d.title }));
    },
  });
}

export function usePhienBanMoi() {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi();
  return useMutation<KnowledgeDocumentDetail, ApiError, { taiLieu: KnowledgeDocument; change_note: string; content_summary: string | null }>({
    mutationFn: ({ taiLieu, change_note, content_summary }) => triThucRepository.phienBanMoi(taiLieu.id, { change_note, content_summary }),
    onSuccess: (d) => {
      lamMoi(KHOA_TRI_THUC.taiLieu);
      toast.success(t('triThuc.taiLieu.daTaoPhienBan', { ten: d.title, phienBan: d.current_version }));
    },
  });
}
